import React, { useState, useReducer } from 'react';
import { BareContentTemplate, BareContentTemplateLegacy } from '@go-shared/types/contentTemplate';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { Plan } from '@go-shared/types/plan';
import { Property } from '@go-shared/types/property';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { BareSailing, BareSailingDay } from '@go-shared/types/sailing';
import { Event } from '@go-shared/types/event';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import {
  CommonTextTranslation,
  MealPeriodTranslation,
  TimeGroupingTranslation,
} from '@go-shared/types/translation';
import { ContentTemplateFormikMode } from '@go-client/components/Formik/ContentTemplateFormik';
import { DailyProgram } from '@go-shared/types/dailyProgram';

export type FeedEditorSettingsPanel =
  | EditVenueRundownSettingsPanel
  | EditVenueMealPeriodsSettingsPanel
  | EditEventSettingsPanel
  | EditContentReplacementsPanel
  | EditMultiDaySettingsPanel
  | EditEventRundownSettingsPanel;

interface EditVenueRundownSettingsPanel {
  type: 'venue-rundown-settings';
  venueRundownId: string;
  selectedVenues: VenueTemplate[];
  feedArea: FeedArea;
  tab: 'venues' | 'order';
}

interface EditVenueMealPeriodsSettingsPanel {
  type: 'venue-meal-periods-settings';
  venueMealPeriodsId: string;
  selectedVenues: VenueTemplate[];
  feedArea: FeedArea;
  tab: 'venues' | 'order';
}

interface EditEventSettingsPanel {
  type: 'event-settings';
  contentTemplate: BareContentTemplateLegacy;
  feedArea: FeedArea;
}

interface EditContentReplacementsPanel {
  type: 'content-settings';
  contentTemplate: BareContentTemplateLegacy;
  feedArea: FeedArea;
}

interface EditMultiDaySettingsPanel {
  type: 'multi-day-settings';
  feedArea: FeedArea;
}

interface EditEventRundownSettingsPanel {
  type: 'event-rundown-settings';
  eventRundownId: string;
  feedArea: FeedArea;
}

interface KBEvent {
  key: string;
  timestamp: number;
  elemId: string;
}

export interface FeedEditorCtxModel {
  contentTemplates: BareContentTemplate[];
  setContentTemplates: (templates: BareContentTemplate[]) => void;
  eventTemplates: EventTemplate[];
  setEventTemplates: (templates: EventTemplate[]) => void;
  lastStyles: React.CSSProperties;
  setEditorElement: (area: FeedArea) => void;
  content: string;
  currentArea?: FeedArea;
  plan?: Plan;
  setPlan: (plan: Plan) => void;
  // Dumb, we should have this in other context
  property?: Property;
  setProperty: (property: Property) => void;
  venueTemplates: VenueTemplate[];
  setVenueTemplates: (vts: VenueTemplate[]) => void;
  events: Event[];
  setEvents: (events: Event[]) => void;
  fonts: Array<{ name: string; file: string }>;
  setFonts: (fonts: Array<{ name: string; file: string }>) => void;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  rundownFontSize: number;
  setRundownFontSize: (rundownFontSize: number) => void;
  sailing?: BareSailing;
  setSailing: (sailing: BareSailing) => void;
  sailingDay?: BareSailingDay;
  setSailingDay: (sailingDay: BareSailingDay) => void;
  settingsPanel: FeedEditorSettingsPanel | undefined;
  setSettingsPanel: (settingsPanel?: FeedEditorSettingsPanel) => void;
  selectedContent: ContentTemplateFormikMode | undefined;
  setSelectedContent: (content?: ContentTemplateFormikMode) => void;
  mpTranslations: MealPeriodTranslation[];
  setMpTranslations: (mpTranslations: MealPeriodTranslation[]) => void;
  timeGroupingTranslations: TimeGroupingTranslation[];
  setTimeGroupingTranslations: (timeGroupingTranslations: TimeGroupingTranslation[]) => void;
  kbEvent?: KBEvent;
  setKbEvent: (e: KBEvent) => void;
  commonTextTranslations: CommonTextTranslation[];
  setCommonTextTranslations: (commonTextTranslations: CommonTextTranslation[]) => void;

  dailyProgram?: DailyProgram;
  setDailyProgram: (dailyProgram: DailyProgram) => void;
}

export const FeedEditorCtx = React.createContext<FeedEditorCtxModel>({
  contentTemplates: [],
  setContentTemplates: () => {
    /* NOOP */
  },
  eventTemplates: [],
  setEventTemplates: () => {
    /* NOOP */
  },
  setEditorElement: () => {
    /* NOOP */
  },
  lastStyles: {},
  content: '',
  setPlan: () => {
    /* NOOP */
  },
  setProperty: () => {
    /* NOOP */
  },
  setVenueTemplates: () => {
    /* NOOP */
  },
  venueTemplates: [],
  setEvents: () => {
    /* NOOP */
  },
  events: [],
  setFonts: () => {
    /* NOOP */
  },
  fonts: [],
  editMode: false,
  setEditMode: () => () => {
    /* NOOP */
  },
  rundownFontSize: 0,
  setRundownFontSize: () => {
    /* NOOP */
  },
  setSailing: () => {
    /* NOOP */
  },
  setSailingDay: () => {
    /* NOOP */
  },
  settingsPanel: undefined,
  setSettingsPanel: () => {
    /* NOOP */
  },
  selectedContent: undefined,
  setSelectedContent: () => {
    /* NOOP */
  },
  kbEvent: undefined,
  setKbEvent: () => {
    /* NOOP */
  },
  mpTranslations: [],
  setMpTranslations: () => {
    /* NOOP */
  },
  timeGroupingTranslations: [],
  setTimeGroupingTranslations: () => {
    /* NOOP */
  },
  commonTextTranslations: [],
  setCommonTextTranslations: () => {
    /* NOOP */
  },
  dailyProgram: undefined,
  setDailyProgram: () => {
    /* NOOP */
  },
});

const PROTECTED_KEYS = ['fontFamily', 'textAlign', 'color'];
const reducer = (state: React.CSSProperties, { key, value }: any) => {
  if (PROTECTED_KEYS.indexOf(key) !== -1) return { ...state, [key]: value };
  return { ...state, [key]: value + 'px' };
};

export const FeedEditorCtxProvider: React.FC = ({ children }) => {
  const [style, setStyle] = useReducer(reducer, {});
  const [contentTemplates, setContentTemplates] = useState<BareContentTemplate[]>([]);
  const [eventTemplates, setEventTemplates] = useState<EventTemplate[]>([]);
  const [plan, setPlan] = useState<Plan | undefined>(undefined);
  const [venueTemplates, setVenueTemplates] = useState<VenueTemplate[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [property, setProperty] = useState<Property | undefined>(undefined);
  const [currentArea, setCurrentArea] = useState<FeedArea | undefined>(undefined);
  const [content, setContent] = useState('');
  const [tabName, setTabName] = useState<'general' | 'spacing'>('general');
  const [fonts, setFonts] = useState<Array<{ name: string; file: string }>>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [rundownFontSize, setRundownFontSize] = useState(14);
  const [sailing, setSailing] = useState<BareSailing | undefined>(undefined);
  const [sailingDay, setSailingDay] = useState<BareSailingDay | undefined>(undefined);
  const [settingsPanel, setSettingsPanel] = useState<FeedEditorSettingsPanel | undefined>(
    undefined
  );
  const [selectedContent, setSelectedContent] = useState<ContentTemplateFormikMode | undefined>(
    undefined
  );
  const [kbEvent, setKbEvent] = useState<KBEvent | undefined>(undefined);
  const [mpTranslations, setMpTranslations] = useState<MealPeriodTranslation[]>([]);
  const [timeGroupingTranslations, setTimeGroupingTranslations] = useState<
    TimeGroupingTranslation[]
  >([]);
  const [commonTextTranslations, setCommonTextTranslations] = useState<CommonTextTranslation[]>([]);
  const [dailyProgram, setDailyProgram] = useState<DailyProgram | undefined>(undefined);

  const setEditorElement = (area: FeedArea) => {
    setCurrentArea(area);
    if (area.contentType === 'text') {
      setContent(area.params!.text!);
    }
  };

  const onStyleChange = (e: any) => {
    setStyle({ key: e.target.name, value: e.target.value });
  };

  const onContentChange = (e: any) => {
    setContent(e.target.value);
  };

  const generalTab = () => {
    return (
      <>
        <div className="w-full">
          <span>Content</span>
          <textarea
            className="mt-2"
            name="content"
            onChange={onContentChange}
            placeholder="Content"
            value={content}
            rows={4}
          />
        </div>
        <div className="w-full">
          <span>Font Size</span>
          <input
            type="number"
            className="mt-2"
            name="fontSize"
            onChange={onStyleChange}
            placeholder="16"
          />
        </div>
        <div className="w-full mt-3">
          <span className="w-full">Font</span>
          <div>
            {fonts.map(font => {
              return (
                <button
                  onClick={() => setStyle({ key: 'fontFamily', value: font.name })}
                  style={{ fontFamily: font.name }}
                  className="w-full text-left"
                >
                  {font.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="w-full mt-3">
          <span className="w-full">Color</span>
          <input onChange={e => setStyle({ key: 'color', value: e.target.value })} />
        </div>
        <div className="w-full mt-3">
          <span className="w-full">Text Align</span>
          <div>
            <button onClick={() => setStyle({ key: 'textAlign', value: 'left' })}>Left</button>
            <button onClick={() => setStyle({ key: 'textAlign', value: 'center' })}>Center</button>
            <button onClick={() => setStyle({ key: 'textAlign', value: 'right' })}>Right</button>
          </div>
        </div>
      </>
    );
  };

  const spacingTab = () => {
    return (
      <>
        <div className="w-full flex flex-row flex-wrap justify-center">
          {/* PADDING */}
          <div
            className="w-3/4 relative inline-flex justify-between mr-auto ml-auto items-center mt-6"
            style={{ height: 100 }}
          >
            <input
              type="number"
              name="paddingLeft"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 text-center"
            />
            <span>Padding</span>
            <input
              type="number"
              name="paddingRight"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 text-center"
            />
            <input
              type="number"
              name="paddingTop"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 absolute text-center"
              style={{ left: 0, top: 0, right: 0, margin: 'auto' }}
            />
            <input
              type="number"
              name="paddingBottom"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 absolute text-center"
              style={{ left: 0, bottom: 0, right: 0, margin: 'auto' }}
            />
          </div>
          <span className="bg-blue-darker w-full mt-6 mb-6" style={{ height: 1, opacity: 0.2 }} />
          {/* MARGIN */}
          <div
            className="w-3/4 relative inline-flex justify-between mr-auto ml-auto items-center "
            style={{ height: 100 }}
          >
            <input
              type="number"
              name="marginLeft"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 text-center"
            />
            <span>Margin</span>
            <input
              type="number"
              name="marginRight"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 text-center"
            />
            <input
              type="number"
              name="marginTop"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 absolute text-center"
              style={{ left: 0, top: 0, right: 0, margin: 'auto' }}
            />
            <input
              type="number"
              name="marginBottom"
              onChange={onStyleChange}
              placeholder="0"
              className="w-8 absolute text-center"
              style={{ left: 0, bottom: 0, right: 0, margin: 'auto' }}
            />
          </div>
        </div>
      </>
    );
  };
  return (
    <FeedEditorCtx.Provider
      value={{
        contentTemplates,
        setContentTemplates,
        eventTemplates,
        setEventTemplates,
        lastStyles: style,
        setEditorElement,
        content,
        currentArea,
        plan,
        setPlan,
        property,
        setProperty,
        venueTemplates,
        setVenueTemplates,
        events,
        setEvents,
        fonts,
        setFonts,
        editMode,
        setEditMode,
        rundownFontSize,
        setRundownFontSize,
        sailingDay,
        setSailingDay,
        sailing,
        setSailing,
        settingsPanel,
        setSettingsPanel,
        selectedContent,
        setSelectedContent,
        kbEvent,
        setKbEvent,
        mpTranslations,
        setMpTranslations,
        timeGroupingTranslations,
        setTimeGroupingTranslations,
        commonTextTranslations: commonTextTranslations,
        setCommonTextTranslations: setCommonTextTranslations,
        dailyProgram,
        setDailyProgram,
      }}
    >
      {children}
      {currentArea && (
        <div
          className="EditorBox fixed shadow-md bg-white px-3 py-4 overflow-scroll"
          style={{ width: 230, height: '40vh', bottom: 20, left: 250, zIndex: 999 }}
        >
          <div className="inline-flex">
            <button
              onClick={() => setTabName('general')}
              className={`${
                tabName === 'general' && 'border-b border-blue-darker'
              } focus:outline-none`}
            >
              General
            </button>
            <button
              onClick={() => setTabName('spacing')}
              className={`${
                tabName === 'spacing' && 'border-b border-blue-darker'
              } ml-8 focus:outline-none`}
            >
              Spacing
            </button>
          </div>
          <div className="w-full">
            {tabName === 'general' && generalTab()}
            {tabName === 'spacing' && spacingTab()}
          </div>
        </div>
      )}
    </FeedEditorCtx.Provider>
  );
};
